import { z } from 'zod';

/**
 * Kettenberechnung je Projekt (VB-26025 Stufe A) — Schemas für den Endpunkt.
 *
 * Erfasst werden nur die Angaben, die es nirgends sonst gibt. Anzahl Räumer und
 * Balkenanzahl kommen aus der Projektauslegung (ADR 0081) und stehen in der
 * Antwort mit dabei, sind aber **nicht** eingebbar — sonst gäbe es zwei
 * Wahrheiten (§6).
 */

export const saveChainCalcSchema = z.object({
  /** Gesamtlänge des Kettenwegs in mm (aus der Splinelänge). */
  chainPathLengthMm: z.number().positive('Die Länge muss größer als 0 sein.').max(1_000_000).nullish(),
  /** Länge der Antriebskette in mm (aus dem Spline). */
  driveChainLengthMm: z.number().positive('Die Länge muss größer als 0 sein.').max(1_000_000).nullish(),
  /** Glieder der Ersatzkette. */
  spareChainLinks: z.number().int().min(0).max(1000),
  /** Gliedlänge der Förderkette in mm (Kettentyp). */
  chainLinkLengthMm: z.number().positive('Die Gliedlänge muss größer als 0 sein.').max(10_000),
  /** Teilung der Antriebskette in mm (Kettentyp). */
  driveChainPitchMm: z.number().positive('Die Teilung muss größer als 0 sein.').max(10_000),
  note: z.string().trim().max(2000).nullish(),
});
export type SaveChainCalcInput = z.infer<typeof saveChainCalcSchema>;

/** Eine Kettenreihe in der Antwort. */
export const chainRowResponseSchema = z.object({
  flights: z.number().int(),
  links: z.number().int(),
  spacingMm: z.number(),
  strands: z.number().int(),
  linksPerStrand: z.number().int(),
  lengthM: z.number(),
});

export const chainCalcResponseSchema = z.object({
  projectId: z.string().uuid(),
  /** Eingaben (gespeichert). */
  input: saveChainCalcSchema,
  /**
   * Aus der Projektauslegung gelesen. `null`, wenn die Zeile dort noch nicht
   * ausgefüllt ist — dann kann nicht gerechnet werden, und die Oberfläche sagt,
   * wo der Wert hingehört.
   */
  fromLayout: z.object({
    scraperCount: z.number().nullable(),
    flightsPerScraper: z.number().nullable(),
  }),
  /** Gerechnetes Ergebnis; null, solange Eingaben fehlen. */
  result: z
    .object({
      totalLinks: z.number().int(),
      totalChainLengthMm: z.number(),
      lengthPerFlightMm: z.number(),
      linksBetweenFlights: z.number(),
      rows: z.tuple([chainRowResponseSchema, chainRowResponseSchema]),
      spare: z.object({ rolls: z.number().int(), links: z.number().int(), lengthM: z.number() }),
      conveyorChainLengthM: z.number(),
      driveChain: z.object({ linksPerDrive: z.number().int(), lengthM: z.number() }),
      issues: z.array(z.string()),
    })
    .nullable(),
  /** Was fehlt, damit gerechnet werden kann — in der Sprache des Blattes. */
  missing: z.array(z.string()),
  updatedAt: z.string().datetime().nullable(),
});
export type ChainCalcResponse = z.infer<typeof chainCalcResponseSchema>;
